import { useRef } from 'react';
import { setRecordReference, unmarkRecordAsOpened } from '../../../services/record.service';
import type { ActiveRecordChildRenderProps } from '../../active-record/active-record.component';
import GroupDetails, { type GroupDetailsProps } from '../../admin/group/group-details.component';
import ModuleDetails from '../../admin/module/module-details.component';
import type { ModuleDetailsProps } from '../../admin/module/module-details.props';
import OccupationDetails, { type OccupationDetailsProps } from '../../admin/occupation/occupation-details.component';
import RiskMatrixDetails, { type RiskMatrixDetailsProps } from '../../admin/risk-matrix/risk-matrix-details';
import RuleDetails, { type RuleDetailsProps } from '../../admin/rule/rule-details.component';
import SchedulerDetails, { type SchedulerDetailsProps } from '../../admin/scheduler/scheduler-details.component';
import CompetencyGroupsDetails, {
  type CompetencyGroupsDetailsProps,
} from '../../admin/training-management/competency-groups/competency-groups-details-component';
import CompetencyDetails, {
  type CompetencyDetailsProps,
} from '../../admin/training-management/competency/competency-details.component';
import OccupationalRequirementsDetails, {
  type OccupationalRequirementsDetailsProps,
} from '../../admin/training-management/occupational-requirements/occupational-requirements-details-component';
import UserDetails, { type UserDetailsProps } from '../../admin/user/user-details.component';
import FormBuilder, { type FormBuilderProps } from '../../form-builder/form-builder.components';
import HierarchyDetails, { type HierarchyDetailsProps } from '../../hierarchy/hierarchy-details.component';
import NotificationDetailsComponent, {
  type NotificationDetailsProps,
} from '../../record-notification/notification-details.component';
import RecordAnalysis, { type RecordAnalysisProps } from '../../record/record-analysis.component';
import Record, { type RecordProps, type RecordRef } from '../../record/record.component';

export default function ActiveSidePanelItem({
  onCloseActiveRecord,
  settingsSubject,
  extraSettings,
  onCloseActiveRecordConfirmationModalRef,
}: ActiveRecordChildRenderProps) {
  const settings = extraSettings as Record<string, unknown>;
  const recordRef = useRef<RecordRef | null>(null);

  if (settings?.userId || settings?.isNewUser === true) {
    return (
      <UserDetails
        {...(extraSettings as UserDetailsProps)}
        onClose={onCloseActiveRecord}
        panelSettings={settingsSubject}
        onCloseActiveRecordConfirmationModalRef={onCloseActiveRecordConfirmationModalRef}
      />
    );
  } else if (settings?.competencyGroupId) {
    return (
      <CompetencyGroupsDetails
        {...(extraSettings as CompetencyGroupsDetailsProps)}
        onClose={onCloseActiveRecord}
        panelSettings={settingsSubject}
        onCloseActiveRecordConfirmationModalRef={onCloseActiveRecordConfirmationModalRef}
      />
    );
  } else if (settings?.competencyId) {
    return (
      <CompetencyDetails
        {...(extraSettings as CompetencyDetailsProps)}
        onClose={onCloseActiveRecord}
        panelSettings={settingsSubject}
        onCloseActiveRecordConfirmationModalRef={onCloseActiveRecordConfirmationModalRef}
      />
    );
  } else if (settings?.formId && !settings?.isRecordAnalysis && !settings?.isFormBuilder) {
    return (
      <Record
        ref={(ref: RecordRef | null) => {
          if (ref) {
            recordRef.current = ref;
            setRecordReference(ref);
          } else if (recordRef.current) {
            unmarkRecordAsOpened(recordRef.current);
            recordRef.current = null;
          }
        }}
        {...(extraSettings as RecordProps)}
        onClose={(id: string) => {
          if (recordRef.current) {
            unmarkRecordAsOpened(recordRef.current);
            recordRef.current = null;
          }
          onCloseActiveRecord(id);
        }}
        panelSettings={settingsSubject}
        onCloseActiveRecordConfirmationModalRef={onCloseActiveRecordConfirmationModalRef}
      />
    );
  } else if (settings?.groupId) {
    return (
      <GroupDetails
        {...(extraSettings as GroupDetailsProps)}
        onClose={onCloseActiveRecord}
        panelSettings={settingsSubject}
        onCloseActiveRecordConfirmationModalRef={onCloseActiveRecordConfirmationModalRef}
      />
    );
  } else if (settings?.occupationId || settings?.isNewOccupation) {
    return (
      <OccupationDetails
        {...(extraSettings as OccupationDetailsProps)}
        onClose={onCloseActiveRecord}
        panelSettings={settingsSubject}
        onCloseActiveRecordConfirmationModalRef={onCloseActiveRecordConfirmationModalRef}
      />
    );
  } else if (settings?.riskMatrixId || settings?.isNewRiskMatrix) {
    return (
      <RiskMatrixDetails
        {...(extraSettings as RiskMatrixDetailsProps)}
        onClose={onCloseActiveRecord}
        panelSettings={settingsSubject}
        onCloseActiveRecordConfirmationModalRef={onCloseActiveRecordConfirmationModalRef}
      />
    );
  } else if (settings?.hierarchyId || settings?.isNewHierarchy) {
    return (
      <HierarchyDetails
        {...(extraSettings as HierarchyDetailsProps)}
        onClose={onCloseActiveRecord}
        onCloseActiveRecordConfirmationModalRef={onCloseActiveRecordConfirmationModalRef}
      />
    );
  } else if (settings?.occupationalRequirementId) {
    return (
      <OccupationalRequirementsDetails
        {...(extraSettings as OccupationalRequirementsDetailsProps)}
        onClose={onCloseActiveRecord}
        panelSettings={settingsSubject}
        onCloseActiveRecordConfirmationModalRef={onCloseActiveRecordConfirmationModalRef}
      />
    );
  } else if (settings?.schedulerId || settings?.isNewScheduler) {
    return (
      <SchedulerDetails
        {...(extraSettings as SchedulerDetailsProps)}
        onClose={onCloseActiveRecord}
        panelSettings={settingsSubject}
        onCloseActiveRecordConfirmationModalRef={onCloseActiveRecordConfirmationModalRef}
      />
    );
  } else if (settings?.notificationId || settings?.isNewNotification) {
    return (
      <NotificationDetailsComponent
        {...(extraSettings as NotificationDetailsProps)}
        onClose={onCloseActiveRecord}
        panelSettings={settingsSubject}
        onCloseActiveRecordConfirmationModalRef={onCloseActiveRecordConfirmationModalRef}
      />
    );
  } else if (settings?.isRecordAnalysis) {
    return <RecordAnalysis {...(extraSettings as RecordAnalysisProps)} onClose={onCloseActiveRecord} />;
  } else if (settings?.ruleId) {
    return (
      <RuleDetails
        {...(extraSettings as RuleDetailsProps)}
        onClose={onCloseActiveRecord}
        panelSettings={settingsSubject}
        onCloseActiveRecordConfirmationModalRef={onCloseActiveRecordConfirmationModalRef}
      />
    );
  } else if (settings?.moduleId || settings?.isNewModule === true) {
    return (
      <ModuleDetails
        {...(extraSettings as ModuleDetailsProps)}
        onClose={onCloseActiveRecord}
        panelSettings={settingsSubject}
        onCloseActiveRecordConfirmationModalRef={onCloseActiveRecordConfirmationModalRef}
      />
    );
  } else if (settings?.isFormBuilder) {
    return (
      <FormBuilder
        {...(extraSettings as FormBuilderProps)}
        onClose={onCloseActiveRecord}
        panelSettings={settingsSubject}
        onCloseActiveRecordConfirmationModalRef={onCloseActiveRecordConfirmationModalRef}
      />
    );
  }

  return null;
}
